"use client";

import {
	RiCalendarLine,
	RiCheckLine,
	RiExpandUpDownLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { Tabs, TabsList, TabsTrigger } from "@workspace/ui/components/tabs";
import { cn } from "@workspace/ui/lib/utils";
import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

interface DaySelectorProps {
	availableDays: string[];
	selectedDay: string;
	onDayChange: (day: string) => void;
	getDayCount?: (day: string) => number;
	className?: string;
	includeAllOption?: boolean;
	allOptionLabel?: string;
	allOptionCount?: number;
}

export function DaySelector({
	availableDays,
	selectedDay,
	onDayChange,
	getDayCount,
	className,
	includeAllOption = false,
	allOptionLabel = "All",
	allOptionCount,
}: DaySelectorProps) {
	const [open, setOpen] = useState(false);

	// Calculate the total number of tabs (days + optional "All")
	const totalTabs = availableDays.length + (includeAllOption ? 1 : 0);

	// Determine if we should show dropdown on mobile (4+ days)
	const shouldShowDropdownOnMobile = availableDays.length >= 4;

	// Format day for dropdown display
	const formatDayForDropdown = useCallback(
		(day: string): string => {
			// For day names like "wednesday", "thursday", etc.
			// Just capitalize the first letter
			return day.charAt(0).toUpperCase() + day.slice(1);
		},
		[],
	);

	// Create options for dropdown
	const dropdownOptions = useMemo(() => {
		const options = [];

		if (includeAllOption) {
			options.push({
				value: "all",
				label: allOptionLabel,
				count:
					allOptionCount !== undefined
						? allOptionCount
						: getDayCount
							? getDayCount("total")
							: 0,
			});
		}

		availableDays.forEach((day) => {
			options.push({
				value: day,
				label: formatDayForDropdown(day),
				count: getDayCount ? getDayCount(day) : 0,
			});
		});

		return options;
	}, [
		availableDays,
		includeAllOption,
		allOptionLabel,
		allOptionCount,
		getDayCount,
		formatDayForDropdown,
	]);

	// Determine the grid class based on total tabs
	const getGridClass = (count: number): string => {
		if (count === 1) return "grid-cols-1";
		if (count <= 2) return "grid-cols-2";
		if (count <= 3) return "grid-cols-3";
		if (count <= 4) return "grid-cols-4";
		if (count <= 5) return "grid-cols-5";
		if (count <= 6) return "grid-cols-6";
		if (count <= 7) return "grid-cols-7";
		// For 8+ tabs, we'll use flex with equal distribution
		return "flex";
	};

	const gridClass = getGridClass(totalTabs);
	const isFlexLayout = gridClass === "flex";

	// Format day name for display
	const formatDayName = (day: string): ReactElement => {
		// For day-of-week format (wednesday, thursday, etc.)
		const initial = day.charAt(0).toUpperCase();
		const abbrev =
			day.substring(0, 3).charAt(0).toUpperCase() +
			day.substring(0, 3).slice(1);

		// Use single letter on mobile when 4 or more tabs
		if (totalTabs >= 4) {
			return (
				<>
					<span className="sm:hidden">{initial}</span>
					<span className="hidden sm:inline">{abbrev}</span>
				</>
			);
		}

		// Use 3-letter abbreviations when less than 4 tabs
		return <span>{abbrev}</span>;
	};

	return (
		<div className={cn("flex-1", className)}>
			{/* Dropdown mode - Show on mobile only when 4+ days */}
			{shouldShowDropdownOnMobile && (
				<div className="sm:hidden">
					<Popover open={open} onOpenChange={setOpen}>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								role="combobox"
								aria-expanded={open}
								className="h-10 w-full justify-between px-3"
							>
								<div className="flex items-center gap-2">
									<RiCalendarLine className="h-4 w-4 shrink-0" />
									<span>
										{dropdownOptions.find((opt) => opt.value === selectedDay)
											?.label || "Select a day"}
									</span>
									{(() => {
										const selectedOption = dropdownOptions.find(
											(opt) => opt.value === selectedDay,
										);
										return selectedOption?.count !== undefined &&
											selectedOption.count > 0 ? (
											<span className="inline-flex items-center justify-center rounded bg-background border px-1 h-4 min-w-[16px] text-[10px] font-medium ml-1">
												{selectedOption.count}
											</span>
										) : null;
									})()}
								</div>
								<RiExpandUpDownLine className="ml-2 h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent
							className="w-[var(--radix-popover-trigger-width)] p-0"
							align="start"
						>
							<Command>
								<CommandList>
									<CommandGroup>
										{dropdownOptions.map((option) => (
											<CommandItem
												key={option.value}
												value={option.value}
												onSelect={() => {
													onDayChange(option.value);
													setOpen(false);
												}}
												className="cursor-pointer"
											>
												<div className="flex items-center justify-between w-full gap-2">
													<span className="truncate">{option.label}</span>
													<div className="flex items-center gap-2 shrink-0">
														{option.count > 0 && (
															<span className="inline-flex items-center justify-center rounded bg-background border px-1 h-4 min-w-[16px] text-[10px] font-medium">
																{option.count}
															</span>
														)}
														<RiCheckLine
															className={cn(
																"h-4 w-4",
																selectedDay === option.value
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
													</div>
												</div>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			)}

			{/* Tab mode - Show on desktop always, mobile for <4 days */}
			<div
				className={cn(shouldShowDropdownOnMobile ? "hidden sm:block" : "block")}
			>
				<Tabs
					value={selectedDay}
					onValueChange={onDayChange}
					className="w-full"
				>
					<TabsList className={cn("grid w-full", gridClass)}>
						{includeAllOption && (
							<TabsTrigger
								value="all"
								className={cn(
									isFlexLayout ? "flex-1" : "w-full",
									"group relative",
								)}
							>
								<span>{allOptionLabel}</span>
								{(getDayCount || allOptionCount !== undefined) && (
									<span
										className={cn(
											"!opacity-100 ml-2 rounded-md px-1.5 py-0.5 font-medium text-xs group-data-[state=inactive]:border group-data-[state=active]:bg-muted group-data-[state=inactive]:bg-transparent",
											totalTabs >= 5 && "hidden sm:inline-flex",
										)}
									>
										{allOptionCount !== undefined
											? allOptionCount
											: getDayCount
												? getDayCount("total")
												: 0}
									</span>
								)}
							</TabsTrigger>
						)}
						{availableDays.map((day) => (
							<TabsTrigger
								key={day}
								value={day}
								className={cn(
									isFlexLayout ? "flex-1" : "w-full",
									"group relative",
								)}
							>
								{formatDayName(day)}
								{getDayCount && (
									<span
										className={cn(
											"!opacity-100 ml-2 rounded-md px-1.5 py-0.5 font-medium text-xs group-data-[state=inactive]:border group-data-[state=active]:bg-muted group-data-[state=inactive]:bg-transparent",
											totalTabs >= 5 && "hidden sm:inline-flex",
										)}
									>
										{getDayCount(day)}
									</span>
								)}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
		</div>
	);
}
